import { AppSettings, RecordingSession } from '@/types';
import { generateId } from '@/utils/helpers';

const SETTINGS_KEY = 'meetsplit_ai_settings_v2';
const RECORDINGS_KEY = 'meetsplit_ai_recordings_v2';

export const DEFAULT_SETTINGS: AppSettings = {
  storagePath: 'C:\\Recordings\\MeetSplitAI',
  theme: 'light',
  defaultQuality: '1080p60',
  autoProcessAfterRecording: true,
  defaultMicId: 'default',
  enableSystemAudio: true,
  cvSensitivity: 7,
  vadSensitivity: 6,
  countdownSeconds: 3,
  hardwareAcceleration: true,
};

export const SAMPLE_RECORDINGS: RecordingSession[] = [
  {
    id: 'rec_sample_gmeet_01',
    title: 'Conference Session — Auto-Spotlight Discussion',
    createdAt: new Date(Date.now() - 3600000 * 24).toISOString(),
    durationSec: 1540, // ~25 mins
    fileSize: 312000000, // ~297 MB
    filePath: 'C:\\Recordings\\MeetSplitAI\\Meeting_Discussion.mp4',
    resolution: { width: 1920, height: 1080 },
    fps: 60,
    audioChannels: 2,
    status: 'ready',
    detectedLayout: 'side_by_side_2',
    isGroupMeeting: true,
    tags: ['group-meeting', 'single-recording'],
    thumbnailUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720" viewBox="0 0 1280 720"><rect width="1280" height="720" fill="%23202124"/><g transform="translate(40, 40)"><rect width="580" height="580" rx="16" fill="%232D3035" stroke="%233c4043" stroke-width="2"/><circle cx="290" cy="240" r="80" fill="%234285F4"/><text x="290" y="260" font-family="system-ui, sans-serif" font-size="64" font-weight="bold" fill="white" text-anchor="middle">S1</text><text x="290" y="380" font-family="system-ui, sans-serif" font-size="28" font-weight="bold" fill="white" text-anchor="middle">Speaker 1 (Host)</text><rect x="20" y="520" width="160" height="40" rx="20" fill="%231E1E1E" opacity="0.8"/><text x="100" y="546" font-family="system-ui, sans-serif" font-size="16" fill="white" text-anchor="middle">🎤 Speaking</text></g><g transform="translate(660, 40)"><rect width="580" height="580" rx="16" fill="%232D3035" stroke="%233c4043" stroke-width="2"/><circle cx="290" cy="240" r="80" fill="%230F9D58"/><text x="290" y="260" font-family="system-ui, sans-serif" font-size="64" font-weight="bold" fill="white" text-anchor="middle">S2</text><text x="290" y="380" font-family="system-ui, sans-serif" font-size="28" font-weight="bold" fill="white" text-anchor="middle">Speaker 2 (Guest)</text><rect x="20" y="520" width="160" height="40" rx="20" fill="%231E1E1E" opacity="0.8"/><text x="100" y="546" font-family="system-ui, sans-serif" font-size="16" fill="white" text-anchor="middle">🎤 Speaking</text></g><rect x="0" y="650" width="1280" height="70" fill="%231E1E1E"/><text x="640" y="692" font-family="system-ui, sans-serif" font-size="18" fill="%23E8EAED" text-anchor="middle">Conference Spotlight Session</text></svg>',
    participants: [
      {
        id: 'p_speaker_1',
        name: 'Speaker 1 (Host)',
        email: 'speaker1@gmail.com',
        role: 'host',
        crop: { x: 0, y: 0, width: 1, height: 1 },
        confidence: 99,
        isIncluded: true,
        activeSpeakerRatio: 56,
        visibilitySegments: [
          { startSec: 0, endSec: 145 },      // Segment 1: 00:00 - 02:25
          { startSec: 320, endSec: 540 },    // Segment 2: 05:20 - 09:00
          { startSec: 720, endSec: 1040 },   // Segment 3: 12:00 - 17:20
          { startSec: 1280, endSec: 1540 }   // Segment 4: 21:20 - 25:40
        ]
      },
      {
        id: 'p_speaker_2',
        name: 'Speaker 2 (Guest)',
        email: 'speaker2@gmail.com',
        role: 'attendee',
        crop: { x: 0, y: 0, width: 1, height: 1 },
        confidence: 97,
        isIncluded: true,
        activeSpeakerRatio: 44,
        visibilitySegments: [
          { startSec: 145, endSec: 320 },    // Segment 1: 02:25 - 05:20
          { startSec: 540, endSec: 720 },    // Segment 2: 09:00 - 12:00
          { startSec: 1040, endSec: 1280 }   // Segment 3: 17:20 - 21:20
        ]
      }
    ],
    exportedClips: [
      {
        participantId: 'p_speaker_1',
        filePath: 'C:\\Recordings\\MeetSplitAI\\Exports\\Speaker1_Isolated_Track.mp4',
        durationSec: 1540,
        fileSize: 152000000
      },
      {
        participantId: 'p_speaker_2',
        filePath: 'C:\\Recordings\\MeetSplitAI\\Exports\\Speaker2_Isolated_Track.mp4',
        durationSec: 1540,
        fileSize: 148000000
      }
    ]
  },
  {
    id: 'rec_sample_gmeet_02',
    title: 'Design Critique — New Desktop Studio UI',
    createdAt: new Date(Date.now() - 3600000 * 72).toISOString(),
    durationSec: 1485, // ~24 mins
    fileSize: 268435456, // ~256 MB
    filePath: 'C:\\Recordings\\MeetSplitAI\\Design_Critique_20261010.mp4',
    resolution: { width: 1920, height: 1080 },
    fps: 30,
    audioChannels: 2,
    status: 'ready',
    detectedLayout: 'speaker_presentation',
    tags: ['screen', 'presentation'],
    thumbnailUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720" viewBox="0 0 1280 720"><rect width="1280" height="720" fill="%231a1d24"/><rect x="40" y="40" width="840" height="640" rx="16" fill="%23232730" stroke="%233a3f4d" stroke-width="2"/><text x="460" y="360" font-family="system-ui, sans-serif" font-size="32" font-weight="bold" fill="%238ab4f8" text-anchor="middle">🖥️ Screen Presentation</text><g transform="translate(910, 40)"><rect width="330" height="305" rx="16" fill="%232d3035" stroke="%234285f4" stroke-width="2"/><circle cx="165" cy="120" r="50" fill="%234285f4"/><text x="165" y="132" font-family="system-ui, sans-serif" font-size="32" font-weight="bold" fill="white" text-anchor="middle">LV</text><text x="165" y="210" font-family="system-ui, sans-serif" font-size="16" font-weight="bold" fill="white" text-anchor="middle">Liam Vance (Host)</text></g><g transform="translate(910, 375)"><rect width="330" height="305" rx="16" fill="%232d3035" stroke="%233c4043" stroke-width="2"/><circle cx="165" cy="120" r="50" fill="%230f9d58"/><text x="165" y="132" font-family="system-ui, sans-serif" font-size="32" font-weight="bold" fill="white" text-anchor="middle">SC</text><text x="165" y="210" font-family="system-ui, sans-serif" font-size="16" font-weight="bold" fill="white" text-anchor="middle">Sarah Connor (Guest)</text></g></svg>',

    participants: [
      {
        id: 'p_201',
        name: 'Liam Vance (Host & Lead Designer)',
        role: 'host',
        crop: { x: 0.72, y: 0.12, width: 0.25, height: 0.25 },
        confidence: 95,
        isIncluded: true,
        activeSpeakerRatio: 68,
        audioSource: 'host',
        audioIsolationMode: 'strict_mute',
        visibilitySegments: [
          { startSec: 0, endSec: 360 },
          { startSec: 640, endSec: 1020 },
          { startSec: 1250, endSec: 1485 }
        ]
      },
      {
        id: 'p_202',
        name: 'Sarah Connor (Guest)',
        role: 'attendee',
        crop: { x: 0.72, y: 0.40, width: 0.25, height: 0.25 },
        confidence: 91,
        isIncluded: true,
        activeSpeakerRatio: 22,
        audioSource: 'guest',
        audioIsolationMode: 'strict_mute',
        visibilitySegments: [
          { startSec: 360, endSec: 640 },
          { startSec: 1020, endSec: 1250 }
        ]
      }
    ]
  },
  {
    id: 'rec_sample_podcast_01',
    title: 'Tech Unfiltered — Future of AI Video Production (S1E1)',
    createdAt: new Date(Date.now() - 3600000 * 12).toISOString(),
    durationSec: 2160, // 36 mins
    fileSize: 450000000,
    filePath: 'C:\\Recordings\\MeetSplitAI\\Podcast_Tech_Unfiltered_S1E1.mp4',
    resolution: { width: 1920, height: 1080 },
    fps: 30,
    audioChannels: 2,
    status: 'ready',
    detectedLayout: 'single_speaker',
    tags: ['podcast', 'host-cam', 'duo-podcast'],
    podcastMetadata: {
      showName: 'Tech Unfiltered',
      episodeTitle: 'Future of AI Video Production',
      episodeNumber: 1,
      seasonNumber: 1,
      hostName: 'Alex Rivers',
      guestName: 'Elena Rostova',
      format: 'vodcast-duo',
      chapters: [],
      notes: ''
    },
    thumbnailUrl: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720" viewBox="0 0 1280 720"><rect width="1280" height="720" fill="%231a0b2e"/><rect x="40" y="40" width="1200" height="640" rx="24" fill="%232b1348" stroke="%239333ea" stroke-width="3"/><circle cx="640" cy="300" r="110" fill="%23a855f7" opacity="0.3"/><circle cx="640" cy="300" r="80" fill="%239333ea"/><text x="640" y="325" font-family="system-ui, sans-serif" font-size="72" text-anchor="middle">🎙️</text><text x="640" y="460" font-family="system-ui, sans-serif" font-size="34" font-weight="bold" fill="white" text-anchor="middle">Tech Unfiltered (S1E1)</text><text x="640" y="505" font-family="system-ui, sans-serif" font-size="20" fill="%23d8b4fe" text-anchor="middle">Host: Alex Rivers • Guest: Elena Rostova</text></svg>',
    participants: [
      {
        id: 'p_pod_host',
        name: 'Alex Rivers (Host)',
        role: 'host',
        crop: { x: 0, y: 0, width: 1, height: 1 },
        confidence: 100,
        isIncluded: true,
        activeSpeakerRatio: 52,
        visibilitySegments: [{ startSec: 0, endSec: 2160 }]
      }
    ]
  }
];

export class StorageService {
  public static getSettings(): AppSettings {
    try {
      const data = localStorage.getItem(SETTINGS_KEY);
      if (data) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(data) };
      }
    } catch (e) {
      console.warn('Failed to read settings from storage', e);
    }
    return DEFAULT_SETTINGS;
  }

  public static saveSettings(settings: Partial<AppSettings>): AppSettings {
    const current = this.getSettings();
    const updated = { ...current, ...settings };
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save settings to storage', e);
    }
    return updated;
  }

  public static getRecordings(): RecordingSession[] {
    try {
      const data = localStorage.getItem(RECORDINGS_KEY);
      if (data !== null) {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } else {
        this.saveRecordings(SAMPLE_RECORDINGS);
        return SAMPLE_RECORDINGS;
      }
    } catch (e) {
      console.warn('Failed to read recordings from storage', e);
    }
    return [];
  }

  public static saveRecordings(recordings: RecordingSession[]): void {
    try {
      localStorage.setItem(RECORDINGS_KEY, JSON.stringify(recordings));
    } catch (e) {
      console.error('Failed to persist recordings list', e);
    }
  }

  public static addRecording(recording: RecordingSession): void {
    const list = this.getRecordings();
    const updated = [recording, ...list.filter(r => r.id !== recording.id)];
    this.saveRecordings(updated);
  }

  public static updateRecording(id: string, updates: Partial<RecordingSession>): RecordingSession | null {
    const list = this.getRecordings();
    const index = list.findIndex(r => r.id === id);
    if (index === -1) return null;

    const updatedSession = { ...list[index], ...updates };
    list[index] = updatedSession;
    this.saveRecordings(list);
    return updatedSession;
  }

  public static deleteRecording(id: string): RecordingSession[] {
    const list = this.getRecordings();
    const filtered = list.filter(r => r.id !== id);
    this.saveRecordings(filtered);
    return filtered;
  }

  public static getRecordingById(id: string): RecordingSession | undefined {
    return this.getRecordings().find(r => r.id === id);
  }

  public static calculateStorageMetrics() {
    const recordings = this.getRecordings();
    const totalBytes = recordings.reduce((sum, r) => sum + (r.fileSize || 0), 0);
    const totalDurationSec = recordings.reduce((sum, r) => sum + (r.durationSec || 0), 0);
    const totalParticipants = recordings.reduce((sum, r) => sum + (r.participants?.length || 0), 0);
    const totalExports = recordings.reduce((sum, r) => sum + (r.exportedClips?.length || 0), 0);

    return {
      totalBytes,
      totalDurationSec,
      totalParticipants,
      totalRecordings: recordings.length,
      totalExports
    };
  }
}
